// Analytics Panel Component - AI öğrenme ve analiz paneli
import { useState, useEffect } from "react";
import { 
  BarChart3, TrendingUp, TrendingDown, ThumbsUp, ThumbsDown,
  MessageSquare, Brain, AlertTriangle, CheckCircle, RefreshCw,
  ChevronDown, ChevronUp, Trash2, ToggleLeft, ToggleRight, Plus,
  Cpu, Sparkles, Zap, Clock, DollarSign, Check, X, Loader2
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const REASON_LABELS = {
  wrong_info: "Yanlış bilgi",
  incomplete: "Eksik cevap",
  unclear: "Anlaşılmaz",
  off_topic: "Konu dışı",
  too_long: "Çok uzun",
  too_short: "Çok kısa",
  inappropriate: "Uygunsuz",
  other: "Diğer"
};

export function AnalyticsPanel({ open, onOpenChange }) {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [negativeFeedbacks, setNegativeFeedbacks] = useState([]);
  const [learningEntries, setLearningEntries] = useState([]);
  const [popularTopics, setPopularTopics] = useState([]);
  const [modelComparison, setModelComparison] = useState([]);
  const [promptSuggestions, setPromptSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedFeedback, setExpandedFeedback] = useState(null);
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  
  // Manual correction form
  const [showAddCorrection, setShowAddCorrection] = useState(false);
  const [newCorrection, setNewCorrection] = useState({ trigger: "", response: "" });
  
  // Prompt improvement form
  const [promptForm, setPromptForm] = useState({ assistant: "kaan", prompt: "" });
  
  // Auto optimization
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationResults, setOptimizationResults] = useState(null);

  useEffect(() => {
    if (open) {
      loadAnalytics();
    }
  }, [open]);

  const loadAnalytics = async () => {
    setIsLoading(true);
    try {
      const [summaryRes, negativeRes, learningRes, topicsRes, modelsRes, suggestionsRes] = await Promise.all([
        axios.get(`${API}/feedback/analytics/summary?days=30`),
        axios.get(`${API}/feedback/analytics/negative-feedbacks?limit=20`),
        axios.get(`${API}/feedback/learning/entries`),
        axios.get(`${API}/feedback/analytics/popular-topics`),
        axios.get(`${API}/feedback/models/comparison`),
        axios.get(`${API}/feedback/prompts/suggestions`)
      ]);
      
      setAnalytics(summaryRes.data);
      setNegativeFeedbacks(negativeRes.data.feedbacks || []);
      setLearningEntries(learningRes.data.entries || []);
      setPopularTopics(topicsRes.data.topics || []);
      setModelComparison(modelsRes.data.models || []);
      setPromptSuggestions(suggestionsRes.data.suggestions || []);
    } catch (error) {
      console.error("Analytics load error:", error);
      toast.error("Analiz verileri yüklenemedi");
    } finally {
      setIsLoading(false);
    }
  };

  const generatePromptImprovement = async () => {
    if (!promptForm.prompt.trim()) {
      toast.warning("Lütfen mevcut prompt'u girin");
      return;
    }
    
    setIsGeneratingPrompt(true);
    try {
      const res = await axios.post(`${API}/feedback/prompts/improve`, null, {
        params: {
          assistant_type: promptForm.assistant,
          current_prompt: promptForm.prompt
        }
      });
      
      if (res.data.success) {
        loadAnalytics();
        setPromptForm({ assistant: "kaan", prompt: "" });
      } else {
        toast.info(res.data.message || "Öneri oluşturulamadı");
      }
    } catch (error) {
      toast.error("Prompt iyileştirme başarısız");
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  const runAutoOptimization = async () => {
    setIsOptimizing(true);
    setOptimizationResults(null);
    try {
      const res = await axios.post(`${API}/feedback/prompts/auto-optimize`);
      setOptimizationResults(res.data.results || []);
      loadAnalytics();
    } catch (error) {
      toast.error("Optimizasyon başarısız");
    } finally {
      setIsOptimizing(false);
    }
  };

  const approvePromptSuggestion = async (suggestionId) => {
    try {
      await axios.post(`${API}/feedback/prompts/approve/${suggestionId}`);
      loadAnalytics();
    } catch (error) {
      toast.error("Onaylama başarısız");
    }
  };

  const toggleLearningEntry = async (entryId, currentActive) => {
    try {
      await axios.put(`${API}/feedback/learning/toggle/${entryId}?is_active=${!currentActive}`);
      loadAnalytics();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const deleteLearningEntry = async (entryId) => {
    if (!window.confirm("Bu kaydı silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/feedback/learning/${entryId}`);
      loadAnalytics();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const addManualCorrection = async () => {
    if (!newCorrection.trigger || !newCorrection.response) {
      toast.warning("Lütfen tüm alanları doldurun");
      return;
    }
    try {
      await axios.post(`${API}/feedback/learning/add-correction`, null, {
        params: {
          trigger_query: newCorrection.trigger,
          improved_response: newCorrection.response,
          pattern_type: "correction"
        }
      });
      setNewCorrection({ trigger: "", response: "" });
      setShowAddCorrection(false);
      loadAnalytics();
    } catch (error) {
      toast.error("Ekleme başarısız");
    }
  };

  const tabs = [
    { id: "overview", label: "Genel Bakış", icon: BarChart3 },
    { id: "models", label: "Model Karşılaştırma", icon: Cpu },
    { id: "prompts", label: "Prompt İyileştirme", icon: Sparkles },
    { id: "negative", label: "Olumsuz Bildirimler", icon: ThumbsDown },
    { id: "learning", label: "Öğrenme", icon: Brain },
    { id: "topics", label: "Konular", icon: MessageSquare }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Brain size={20} className="text-purple-500" />
            AI Öğrenme & Analiz Paneli
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-zinc-800 pb-3">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                activeTab === tab.id
                  ? "bg-purple-600 text-white"
                  : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={loadAnalytics}
            disabled={isLoading}
            className="ml-auto text-zinc-400"
          >
            <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto pr-2">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw size={32} className="animate-spin text-purple-500" />
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === "overview" && analytics && (
                <div className="space-y-6">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-4 gap-4">
                    <StatCard
                      label="Toplam Geri Bildirim"
                      value={analytics.total_feedbacks}
                      icon={MessageSquare}
                      color="blue"
                    />
                    <StatCard
                      label="Olumlu"
                      value={analytics.positive_feedbacks}
                      icon={ThumbsUp}
                      color="green"
                    />
                    <StatCard
                      label="Olumsuz"
                      value={analytics.negative_feedbacks}
                      icon={ThumbsDown}
                      color="red"
                    />
                    <StatCard
                      label="Memnuniyet"
                      value={`%${analytics.satisfaction_rate}`}
                      icon={analytics.satisfaction_rate >= 70 ? TrendingUp : TrendingDown}
                      color={analytics.satisfaction_rate >= 70 ? "green" : "yellow"}
                    />
                  </div>

                  {/* Assistant Performance */}
                  <div className="bg-zinc-800/50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-zinc-300 mb-3">Asistan Performansı</h3>
                    <div className="space-y-2">
                      {Object.entries(analytics.assistant_performance || {}).map(([name, data]) => (
                        <div key={name} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                          <span className="text-white capitalize">{name}</span>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-zinc-400">{data.total} değerlendirme</span>
                            <span className={`font-medium ${data.satisfaction_rate >= 70 ? "text-green-400" : "text-yellow-400"}`}>
                              %{data.satisfaction_rate} memnuniyet
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Negative Reasons Breakdown */}
                  {Object.keys(analytics.negative_reasons_breakdown || {}).length > 0 && (
                    <div className="bg-zinc-800/50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-zinc-300 mb-3">Olumsuz Geri Bildirim Nedenleri</h3>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(analytics.negative_reasons_breakdown).map(([reason, count]) => (
                          <div key={reason} className="flex items-center justify-between p-2 bg-zinc-800 rounded">
                            <span className="text-zinc-300 text-sm">{REASON_LABELS[reason] || reason}</span>
                            <span className="text-red-400 font-medium">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Popular Questions */}
                  {analytics.popular_questions?.length > 0 && (
                    <div className="bg-zinc-800/50 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-zinc-300 mb-3">En Çok Sorulan Sorular</h3>
                      <div className="space-y-2">
                        {analytics.popular_questions.slice(0, 5).map((q, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-zinc-800 rounded">
                            <span className="text-zinc-300 text-sm truncate flex-1">{q.question}</span>
                            <span className="text-blue-400 font-medium ml-2">{q.count}x</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Models Tab */}
              {activeTab === "models" && (
                <div className="space-y-4">
                  <p className="text-zinc-400 text-sm">
                    Modellerin performans karşılaştırması ve otomatik seçim
                  </p>
                  
                  <div className="grid gap-3">
                    {modelComparison.map((model) => (
                      <div 
                        key={model.model_id}
                        className={`bg-zinc-800/50 rounded-lg p-4 border-l-4 ${
                          model.is_active ? "border-green-500" : "border-zinc-700"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-medium text-white">{model.name}</span>
                              <span className="text-xs px-2 py-0.5 rounded bg-zinc-700 text-zinc-300">
                                {model.provider}
                              </span>
                              {model.is_active && (
                                <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                                  Aktif
                                </span>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-4 gap-4 mt-3 text-sm">
                              <div className="flex items-center gap-2">
                                <Zap size={14} className="text-yellow-400" />
                                <span className="text-zinc-400">Hız:</span>
                                <span className="text-white">{model.speed_rating}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <CheckCircle size={14} className="text-green-400" />
                                <span className="text-zinc-400">Kalite:</span>
                                <span className="text-white">{model.quality_rating}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <DollarSign size={14} className="text-blue-400" />
                                <span className="text-zinc-400">Maliyet:</span>
                                <span className="text-white">${model.cost}/1K</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock size={14} className="text-purple-400" />
                                <span className="text-zinc-400">Ort. Süre:</span>
                                <span className="text-white">{model.avg_response_time}s</span>
                              </div>
                            </div>
                            
                            {model.total_queries > 0 && (
                              <div className="mt-3 flex items-center gap-4 text-sm">
                                <span className="text-zinc-500">{model.total_queries} sorgu</span>
                                <span className={`font-medium ${model.positive_rate >= 70 ? "text-green-400" : "text-yellow-400"}`}>
                                  %{model.positive_rate} memnuniyet
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {modelComparison.length === 0 && (
                    <div className="text-center py-12 text-zinc-500">
                      <Cpu size={48} className="mx-auto mb-3" />
                      <p>Model verisi yok</p>
                    </div>
                  )}
                </div>
              )}

              {/* Prompts Tab */}
              {activeTab === "prompts" && (
                <div className="space-y-4">
                  <p className="text-zinc-400 text-sm">
                    AI ile otomatik prompt iyileştirme önerileri oluşturun
                  </p>
                  
                  {/* Generate Prompt Improvement */}
                  <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-700/50 rounded-lg p-4 space-y-3">
                    <h4 className="text-sm font-medium text-purple-300 flex items-center gap-2">
                      <Sparkles size={16} />
                      Yeni İyileştirme Oluştur
                    </h4>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <label className="text-xs text-zinc-400">Asistan</label>
                        <select
                          value={promptForm.assistant}
                          onChange={(e) => setPromptForm({ ...promptForm, assistant: e.target.value })}
                          className="w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-white text-sm"
                        >
                          <option value="kaan">Kaan</option>
                          <option value="defne">Defne</option>
                        </select>
                      </div>
                      <div className="col-span-3">
                        <label className="text-xs text-zinc-400">Mevcut Sistem Prompt'u</label>
                        <Textarea
                          value={promptForm.prompt}
                          onChange={(e) => setPromptForm({ ...promptForm, prompt: e.target.value })}
                          placeholder="İyileştirilecek mevcut prompt'u yapıştırın..."
                          className="bg-zinc-800 border-zinc-700 mt-1"
                          rows={2}
                        />
                      </div>
                    </div>
                    <Button
                      onClick={generatePromptImprovement}
                      disabled={isGeneratingPrompt}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {isGeneratingPrompt ? (
                        <>
                          <RefreshCw size={16} className="mr-2 animate-spin" />
                          Analiz ediliyor...
                        </>
                      ) : (
                        <>
                          <Sparkles size={16} className="mr-2" />
                          AI ile İyileştir
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Pending Suggestions */}
                  <div>
                    <h4 className="text-sm font-medium text-zinc-300 mb-3">Onay Bekleyen Öneriler</h4>
                    {promptSuggestions.length === 0 ? (
                      <div className="text-center py-8 text-zinc-500 bg-zinc-800/30 rounded-lg">
                        <Sparkles size={32} className="mx-auto mb-2" />
                        <p>Henüz öneri yok</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {promptSuggestions.map((suggestion, i) => (
                          <div key={i} className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
                            <div className="flex items-start justify-between mb-3">
                              <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded">
                                {suggestion.assistant_type}
                              </span>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => approvePromptSuggestion(suggestion._id)}
                                  className="text-green-400 hover:text-green-300"
                                >
                                  <Check size={16} className="mr-1" />
                                  Onayla
                                </Button>
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              <div>
                                <span className="text-xs text-zinc-500">Değişiklikler:</span>
                                <ul className="mt-1 space-y-1">
                                  {suggestion.changes?.map((change, j) => (
                                    <li key={j} className="text-sm text-zinc-300 flex items-start gap-2">
                                      <span className="text-green-400">•</span>
                                      {change}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              
                              {suggestion.reasoning && (
                                <div>
                                  <span className="text-xs text-zinc-500">Gerekçe:</span>
                                  <p className="text-sm text-zinc-400 mt-1">{suggestion.reasoning}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Negative Feedbacks Tab */}
              {activeTab === "negative" && (
                <div className="space-y-3">
                  {negativeFeedbacks.length === 0 ? (
                    <div className="text-center py-12 text-zinc-500">
                      <CheckCircle size={48} className="mx-auto mb-3 text-green-500" />
                      <p>Henüz olumsuz geri bildirim yok</p>
                    </div>
                  ) : (
                    negativeFeedbacks.map((fb, i) => (
                      <div key={i} className="bg-zinc-800/50 rounded-lg p-4">
                        <div 
                          className="flex items-start justify-between cursor-pointer"
                          onClick={() => setExpandedFeedback(expandedFeedback === i ? null : i)}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-red-400 text-xs px-2 py-0.5 bg-red-500/20 rounded">
                                {REASON_LABELS[fb.negative_reason] || fb.negative_reason}
                              </span>
                              <span className="text-zinc-500 text-xs">{fb.assistant_type}</span>
                            </div>
                            <p className="text-zinc-300 text-sm truncate">{fb.user_query}</p>
                          </div>
                          {expandedFeedback === i ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </div>
                        
                        {expandedFeedback === i && (
                          <div className="mt-3 pt-3 border-t border-zinc-700 space-y-2">
                            <div>
                              <span className="text-xs text-zinc-500">Kullanıcı Sorusu:</span>
                              <p className="text-zinc-300 text-sm mt-1">{fb.user_query}</p>
                            </div>
                            <div>
                              <span className="text-xs text-zinc-500">AI Cevabı:</span>
                              <p className="text-zinc-400 text-sm mt-1 max-h-32 overflow-y-auto">{fb.ai_response}</p>
                            </div>
                            {fb.comment && (
                              <div>
                                <span className="text-xs text-zinc-500">Kullanıcı Yorumu:</span>
                                <p className="text-yellow-400 text-sm mt-1">{fb.comment}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Learning Tab */}
              {activeTab === "learning" && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <p className="text-zinc-400 text-sm">
                      AI'ın öğrendiği pattern'lar ve düzeltmeler
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={runAutoOptimization}
                        disabled={isOptimizing}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {isOptimizing ? (
                          <Loader2 size={16} className="mr-1 animate-spin" />
                        ) : (
                          <Sparkles size={16} className="mr-1" />
                        )}
                        Otomatik Optimize Et
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setShowAddCorrection(true)}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        <Plus size={16} className="mr-1" />
                        Manuel Düzeltme
                      </Button>
                    </div>
                  </div>

                  {/* Optimization Results */}
                  {optimizationResults && (
                    <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-green-300 mb-2">Optimizasyon Sonuçları</h4>
                      {optimizationResults.map((result, i) => (
                        <div key={i} className="text-sm text-zinc-300 mb-1">
                          <span className="font-medium text-green-400">{result.assistant}:</span>{" "}
                          {result.status === "suggestion_created" 
                            ? `${result.changes?.length || 0} değişiklik önerildi`
                            : "Sorun bulunamadı"}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Correction Form */}
                  {showAddCorrection && (
                    <div className="bg-purple-900/30 border border-purple-700 rounded-lg p-4 space-y-3">
                      <h4 className="text-sm font-medium text-purple-300">Yeni Düzeltme Ekle</h4>
                      <div>
                        <label className="text-xs text-zinc-400">Bu soru sorulduğunda:</label>
                        <Input
                          value={newCorrection.trigger}
                          onChange={(e) => setNewCorrection({ ...newCorrection, trigger: e.target.value })}
                          placeholder="Örn: Türkiye'nin başkenti neresi?"
                          className="bg-zinc-800 border-zinc-700 mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-400">Bu cevabı ver:</label>
                        <Textarea
                          value={newCorrection.response}
                          onChange={(e) => setNewCorrection({ ...newCorrection, response: e.target.value })}
                          placeholder="Doğru/iyileştirilmiş cevap..."
                          className="bg-zinc-800 border-zinc-700 mt-1"
                          rows={3}
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="ghost" onClick={() => setShowAddCorrection(false)}>
                          İptal
                        </Button>
                        <Button onClick={addManualCorrection} className="bg-purple-600">
                          Ekle
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Learning Entries List */}
                  {learningEntries.length === 0 ? (
                    <div className="text-center py-12 text-zinc-500">
                      <Brain size={48} className="mx-auto mb-3" />
                      <p>Henüz öğrenilen pattern yok</p>
                      <p className="text-xs mt-1">Geri bildirimler toplandıkça AI öğrenmeye başlayacak</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {learningEntries.map((entry) => (
                        <div 
                          key={entry.id} 
                          className={`bg-zinc-800/50 rounded-lg p-4 border-l-4 ${
                            entry.is_active ? "border-green-500" : "border-zinc-600"
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  entry.pattern_type === "correction" 
                                    ? "bg-blue-500/20 text-blue-400"
                                    : "bg-yellow-500/20 text-yellow-400"
                                }`}>
                                  {entry.pattern_type === "correction" ? "Manuel Düzeltme" : "Öğrenildi"}
                                </span>
                                <span className="text-xs text-zinc-500">
                                  {entry.feedback_count} geri bildirim
                                </span>
                              </div>
                              <p className="text-zinc-300 text-sm">{entry.trigger_query}</p>
                              {entry.improved_response && (
                                <p className="text-green-400 text-sm mt-2 pl-3 border-l-2 border-green-600">
                                  {entry.improved_response}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleLearningEntry(entry.id, entry.is_active)}
                                className={entry.is_active ? "text-green-400" : "text-zinc-500"}
                              >
                                {entry.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteLearningEntry(entry.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Topics Tab */}
              {activeTab === "topics" && (
                <div className="space-y-4">
                  <p className="text-zinc-400 text-sm">
                    Son 30 günde en çok konuşulan konular
                  </p>
                  {popularTopics.length === 0 ? (
                    <div className="text-center py-12 text-zinc-500">
                      <MessageSquare size={48} className="mx-auto mb-3" />
                      <p>Henüz yeterli veri yok</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {popularTopics.map((topic, i) => (
                        <div 
                          key={i} 
                          className="bg-zinc-800/50 rounded-lg p-3 flex items-center justify-between"
                        >
                          <span className="text-zinc-300 text-sm">{topic.topic}</span>
                          <span className="text-blue-400 font-medium text-sm">{topic.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Stat Card Component
function StatCard({ label, value, icon: Icon, color }) {
  const colors = {
    blue: "text-blue-400 bg-blue-500/20",
    green: "text-green-400 bg-green-500/20",
    red: "text-red-400 bg-red-500/20",
    yellow: "text-yellow-400 bg-yellow-500/20",
    purple: "text-purple-400 bg-purple-500/20"
  };

  return (
    <div className="bg-zinc-800/50 rounded-lg p-4">
      <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center mb-3`}>
        <Icon size={20} />
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-xs text-zinc-500 mt-1">{label}</p>
    </div>
  );
}

export default AnalyticsPanel;
